// @flow

import type {
  Config as BaseConfig,
  Location,
  Node,
  NodeType,
  RenderNode,
  Schema,
  SchemaAttribute,
  ValidationError,
} from '@markdoc/markdoc';

import * as Components from 'docs/components';

import type {ValidationMetadata} from '../../../manifest/validators/getValidationConfig';

type ComponentName = $Keys<typeof Components>;

export type Config = {|
  nodes: $NonMaybeType<$PropertyType<BaseConfig, 'nodes'>>,
  tags: $NonMaybeType<$PropertyType<BaseConfig, 'tags'>>,
  variables: $NonMaybeType<$PropertyType<BaseConfig, 'variables'>>,
  functions: $NonMaybeType<$PropertyType<BaseConfig, 'functions'>>,
  partials: $NonMaybeType<$PropertyType<BaseConfig, 'partials'>>,
  debug: $NonMaybeType<$PropertyType<BaseConfig, 'debug'>>,
  text: string,
  services: {
    [name: string]: any,
  },
  validationMetadata?: ValidationMetadata,
  glossary?: {[string]: {[string]: mixed}},
|};

export type MarkdocValidationError = $Shape<{
  type: string,
  error: ValidationError,
  lines: Array<number>,
  location: Location,
  path?: string,
}>;

type CommonProperties = {|
  ...$Exact<Schema>,
  attributes?: {
    [name: string]: {
      ...SchemaAttribute,
      matches?:
        | RegExp
        | Array<string>
        | ((Config) => ?(RegExp | Array<string>)),
    },
  },
  localize?: boolean,
  description?: string,
  render?: (node: Node, config: Config) => RenderNode | Array<RenderNode>,
  validate?: (node: Node) => Array<ValidationError>,
|};

/**
 * Indicates that the specified React component should be rendered whenever
 * a node of the specified kind occurs in the Markdoc content. Nodes are
 * low-level concepts like "paragraph" or "heading" which are built into
 * the Markdoc syntax itself.
 */
export type MarkdocNodeRegistration = {|
  ...CommonProperties,
  node: NodeType,
  component: ComponentName,
|};

/**
 * Indicates that the specified React component should be rendered whenever
 * the specified tag is encountered. A tag appears in Markdoc like {% foo %}.
 */
export type MarkdocTagRegistration = {|
  ...CommonProperties,
  tag: string,
  component: ComponentName,
|};

/**
 * Indicates that a React.Fragment should be rendered whenever the specified
 * tag is encountered. The render() method will be called and can manipulate
 * the Markdoc AST. This can be used when you want to create a "placeholder"
 * component which doesn't actually render content itself. (For an example,
 * see LineHighlight.markdoc.js for the registration of {% highlight %}.)
 */
export type MarkdocFragmentRegistration = {|
  ...CommonProperties,
  tag: string,
|};

export type MarkdocRegistration =
  | MarkdocNodeRegistration
  | MarkdocTagRegistration
  | MarkdocFragmentRegistration;
